import { useQuery } from '@tanstack/react-query';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslations } from 'use-intl';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { CinemaPartnerType } from '@cineamo/api/models/cinema-partner/CinemaPartnerType.enum';

import { getCinemaFaqs } from '@/utils/api/cinema-faq/cinema-faq-api';
import { getCinemaPartners } from '@/utils/api/cinema-partner/cinema-partner-api';

import { IconInfo24 } from '@cineamo/ui/src/icons/ic-info';
import { IconInfoSolid24 } from '@cineamo/ui/src/icons/ic-infoSolid';

import { useCinemaRooms } from '@/providers/CinemaRoomProvider';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

import NavigationItem from './NavigationItem';
import NavigationItemMenu from './NavigationItemMenu';

const NavigationItemInfo: React.FC<{
    cinemaId: CinemaDto['id'];
}> = ({ cinemaId }) => {
    const t = useTranslations();

    const { cinemaRooms } = useCinemaRooms();
    const hasCinemaRooms = cinemaRooms.length > 0;
    const hasPriceTable = Boolean(useDomainConfiguration().cinema.priceTablePdfUrl);

    const { data: [hasParkings, hasBarsRestaurants, hasFaqs, hasMiscellaneousPartners] = [] } = useQuery({
        queryKey: ['navigation-info-visibilities', cinemaId],
        queryFn: async () => {
            const responses = await Promise.all([
                getCinemaPartners({
                    cinemaIds: [cinemaId],
                    types: [CinemaPartnerType.PARKING]
                }),
                getCinemaPartners({
                    cinemaIds: [cinemaId],
                    types: [CinemaPartnerType.BAR, CinemaPartnerType.RESTAURANT]
                }),
                getCinemaFaqs({
                    cinemaIds: [cinemaId]
                }),
                getCinemaPartners({
                    cinemaIds: [cinemaId],
                    types: [CinemaPartnerType.MISCELLANEOUS]
                })
            ]);

            return responses.map((response) => (response?._total_items ?? 0) > 0);
        }
    });

    const menuItems = useMemo(
        () =>
            [
                { href: 'about', text: t('navigation.info-item.about') },
                {
                    href: 'opening-hours-and-prices',
                    text: hasPriceTable
                        ? t('navigation.info-item.opening-hours-and-prices')
                        : t('navigation.info-item.opening-hours')
                },
                hasCinemaRooms && { href: 'rooms', text: t('navigation.info-item.rooms') },
                hasBarsRestaurants && {
                    href: 'bars-and-restaurants',
                    text: t('navigation.info-item.bars-and-restaurants')
                },
                hasParkings && { href: 'parking', text: t('navigation.info-item.parking') },
                hasFaqs && { href: 'FAQ', text: t('navigation.info-item.faq') },
                hasMiscellaneousPartners && {
                    href: 'partners',
                    text: t('navigation.info-item.partners')
                }
            ].filter((item): item is { text: string; href: string } => item !== false),
        [hasParkings, hasBarsRestaurants, hasFaqs, hasCinemaRooms, hasMiscellaneousPartners, hasPriceTable, t]
    );

    return (
        <NavigationItemMenu
            href={'/info'}
            items={menuItems}
            trigger={
                <NavigationItem
                    href={'/info'}
                    icon={<IconInfo24 />}
                    activeIcon={<IconInfoSolid24 />}
                    title={t('navigation.info')}
                    item="info"
                />
            }
        />
    );
};

export default NavigationItemInfo;
